import { useLayoutEffect, useRef, useState } from 'react';

interface IOptions {
  mutationObserverAttributes?: string[];
  transitionProperties?: string[];
  enableMutationObserver?: boolean;  // Option to enable or disable the mutation observer
  enableTransitionListener?: boolean; // Option to enable or disable the transition listener
}

/**
 * Custom hook to get the width of a component and store it in a CSS variable.
 * This hook listens for changes to the component's children, attributes, and completion of transitions.
 * @param cssVarName The name of the CSS variable to store the width in.
 * @param options Options to specify which mutations and transitions to observe.
 * @returns An object containing the ref to be attached to the component and the current width.
 */
export function useComponentWidth(cssVarName: string, options?: IOptions): { ref: React.RefObject<HTMLDivElement>; width: number } {
  // Define default options
  const defaultOptions: IOptions = {
    mutationObserverAttributes: ['class'],
    transitionProperties: ['width'],
    enableMutationObserver: true, // Default to true
    enableTransitionListener: false // Default to false
  };

  // Merge default options with provided options
  const finalOptions = { ...defaultOptions, ...options };

  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Function to update width
    const updateWidth = () => {
      const newWidth = element.offsetWidth;
      setWidth(newWidth);
      document.documentElement.style.setProperty(`--${cssVarName}`, `${newWidth}px`);
    };

    // Initial width update
    updateWidth();

    let observer: MutationObserver;
    if (finalOptions.enableMutationObserver) {
      // Initialize mutation observer to watch for changes in the component
      observer = new MutationObserver((mutations) => {
        let shouldUpdateWidth = false;

        for (const mutation of mutations) {
          if ((mutation.type === 'attributes' && finalOptions.mutationObserverAttributes.includes(mutation.attributeName)) || mutation.type === 'childList') {
            shouldUpdateWidth = true;
          }
        }

        if (shouldUpdateWidth) {
          updateWidth();
        }
      });

      observer.observe(element, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: finalOptions.mutationObserverAttributes
      });
    }

    if (finalOptions.enableTransitionListener) {
      // Listen for transition end to update width after transitions
      const handleTransitionEnd = (event: TransitionEvent) => {
        if (finalOptions.transitionProperties.includes(event.propertyName)) {
          updateWidth();
        }
      };

      element.addEventListener('transitionend', handleTransitionEnd);

      // Clean up transition listener
      return () => {
        if (observer) observer.disconnect();
        element.removeEventListener('transitionend', handleTransitionEnd);
      };
    }

    // Update width when the window is resized
    window.addEventListener('resize', updateWidth);

    // Clean up resize listener and any other initializations
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [cssVarName, finalOptions]);  // Include finalOptions in the dependency array

  return { ref, width };
}
